import { CircleAlert, TriangleAlert } from 'lucide-preact'
import { type ActivationVersionState, formatSignedOffDate } from '@/utils/emailActivationVersion'

/**
 * Which saved version of its email the activation sends, and whether it may (ORB-303). It sends only a signed-off
 * version; signing a newer one off moves it, so a newer version waiting for sign-off changes nothing yet.
 */
export function ActivationVersionNote({ state, isNew }: { state: ActivationVersionState; isNew: boolean }) {
    const { versionNumber, signedOffAt, newerVersionNumber } = state

    if (!signedOffAt) {
        return (
            <p className="flex items-start gap-2 rounded-lg px-3 py-2 text-xs bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                <CircleAlert className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
                <span>
                    {versionNumber === 0
                        ? "This activation doesn't name a version of its email, so it won't send. Saving it takes the email's latest version."
                        : isNew
                            ? `v${versionNumber} of this email isn't signed off, so an activation can't send it yet. Sign the email off first.`
                            : `v${versionNumber} isn't signed off, so this activation won't send until the email is signed off again.`}
                </span>
            </p>
        )
    }

    return (
        <div className="space-y-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">
                {isNew ? 'Will send' : 'Sends'} v{versionNumber} · signed off {formatSignedOffDate(signedOffAt)}
            </p>
            {newerVersionNumber && (
                <p className="flex items-start gap-2 rounded-lg px-3 py-2 text-xs bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                    <TriangleAlert className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
                    <span>
                        v{newerVersionNumber} is waiting for sign-off. This activation keeps sending v{versionNumber} until v{newerVersionNumber} is signed off.
                    </span>
                </p>
            )}
        </div>
    )
}
